import { useState } from 'react'
import { api, errorMessage, useQuery } from '../api.js'
import { Alert, Button, Panel, Pill, PowerPill } from '../components/ui.js'
import { StepUpModal, type StepUpRequest } from '../components/StepUpModal.js'

type Server = Awaited<ReturnType<typeof api.fleet.servers.query>>[number]

export function Fleet() {
  const [stepUp, setStepUp] = useState<StepUpRequest | null>(null)
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn' | 'crit'; text: string } | null>(null)

  // Polling de 10 s: durante uma transicao de energia o estado muda sozinho,
  // e ficar apertando F5 num painel de infraestrutura e sinal de painel ruim.
  const servers = useQuery(() => api.fleet.servers.query(), [], { pollMs: 10_000 })
  const providers = useQuery(() => api.fleet.providers.query(), [], { pollMs: 30_000 })

  /**
   * Monta a frase de impacto a partir do catalogo.
   *
   * Ela entra na confirmacao ANTES de a pessoa digitar o hostname. Descobrir
   * pelo telefone que o Portal caiu junto e o custo de nao ter isto aqui — e o
   * catalogo ja sabe a resposta.
   */
  async function frasePodeCair(serverId: string): Promise<string> {
    try {
      const i = await api.catalog.serverImpact.query({ serverId })
      if (!i.diretos.length && !i.emCascata.length) {
        return 'Nenhum produto do catalogo esta amarrado a este servidor — o que nao garante que nao haja nada rodando nele, so que ninguem cadastrou.'
      }
      const partes: string[] = []
      if (i.diretos.length) {
        partes.push(
          'Cai junto: ' +
            i.diretos.map((d) => `${d.nome} (${d.ambiente})`).join(', ') +
            '.',
        )
      }
      if (i.emCascata.length) {
        partes.push('Em cascata: ' + i.emCascata.map((c) => c.nome).join(', ') + '.')
      }
      return partes.join(' ')
    } catch {
      // O catalogo nao pode impedir uma acao de energia. Sem ele, segue sem a
      // frase — mas dizendo que seguiu sem.
      return 'Nao foi possivel consultar o catalogo agora; siga sabendo que o impacto nao foi conferido.'
    }
  }

  async function act(
    server: Server,
    action: 'on' | 'off' | 'restart',
  ) {
    const labels = { on: 'Ligar', off: 'Desligar', restart: 'Reiniciar' }
    const impacto = action === 'on' ? '' : ` ${await frasePodeCair(server.id)}`
    setStepUp({
      action: `server.power.${action}`,
      title: `${labels[action]} ${server.hostname}`,
      description:
        (action === 'on'
          ? 'A maquina sera ligada pelo provedor.'
          : action === 'off'
            ? 'A maquina sera desligada. Tudo que roda nela fica indisponivel ate ser ligada de novo.'
            : 'A maquina sera reiniciada. Servicos ficam indisponiveis durante o boot.') + impacto,
      confirmHostname: action === 'on' ? undefined : server.hostname,
      onProved: async (proofId) => {
        try {
          const res =
            action === 'on'
              ? await api.fleet.powerOn.mutate({ serverId: server.id, stepUpProofId: proofId })
              : action === 'off'
                ? await api.fleet.powerOff.mutate({
                    serverId: server.id,
                    stepUpProofId: proofId,
                    confirmHostname: server.hostname,
                  })
                : await api.fleet.powerRestart.mutate({
                    serverId: server.id,
                    stepUpProofId: proofId,
                    confirmHostname: server.hostname,
                  })
          setNotice({ tone: res.route === 'manual' ? 'warn' : 'ok', text: res.message })
          void servers.refetch()
        } catch (err) {
          setNotice({ tone: 'crit', text: errorMessage(err) })
        }
      },
    })
  }

  return (
    <div className="space-y-6">
      {notice && (
        <Alert tone={notice.tone}>
          <div className="flex items-start justify-between gap-4">
            <span>{notice.text}</span>
            <button onClick={() => setNotice(null)} className="shrink-0 text-xs underline">
              fechar
            </button>
          </div>
        </Alert>
      )}

      <Panel
        title="Provedores"
        action={
          <Button
            variant="ghost"
            onClick={async () => {
              for (const p of providers.data ?? []) {
                try {
                  await api.fleet.syncProvider.mutate({ providerId: p.id })
                } catch (err) {
                  setNotice({ tone: 'crit', text: errorMessage(err) })
                }
              }
              void servers.refetch()
              void providers.refetch()
            }}
          >
            Sincronizar agora
          </Button>
        }
      >
        {providers.data?.length ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {providers.data.map((p) => (
              <div key={p.id} className="rounded border border-line p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{p.label}</span>
                  <Pill tone={p.status === 'ok' ? 'ok' : 'crit'}>{p.status}</Pill>
                </div>
                <p className="mt-1 font-mono text-xs text-ink-3">{p.kind}</p>
                <p className="mt-3 font-mono text-[11px] text-ink-3">
                  {/* A matriz de capacidades e o coracao da Parte I: mostrar o
                      que cada API sabe fazer evita prometer botao que nao age. */}
                  ligar {p.capabilities.powerOn ? '✓' : '✗'} · desligar{' '}
                  {p.capabilities.powerOff ? '✓' : '✗'} · reiniciar{' '}
                  {p.capabilities.powerRestart ? '✓' : '✗'} · snapshot{' '}
                  {p.capabilities.createSnapshot ? '✓' : '✗'}
                </p>
                {p.lastError && <p className="mt-2 text-xs text-crit">{p.lastError}</p>}
                <p className="mt-2 text-[11px] text-ink-3">
                  {p.lastSyncAt
                    ? `sincronizado ${new Date(p.lastSyncAt).toLocaleString('pt-BR')}`
                    : 'nunca sincronizado'}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-ink-3">
            Nenhum provedor cadastrado ainda. Adicione Kamatera, Locaweb ou Hostinger para o
            inventario aparecer aqui.
          </p>
        )}
      </Panel>

      <Panel title={`Frota — ${servers.data?.length ?? 0} servidores`}>
        {servers.error && <Alert>{servers.error}</Alert>}
        {servers.loading && !servers.data && <p className="text-sm text-ink-3">Carregando...</p>}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-line text-left font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
                <th className="py-2 pr-4">Servidor</th>
                <th className="py-2 pr-4">Provedor</th>
                <th className="py-2 pr-4">SO</th>
                <th className="py-2 pr-4">IP</th>
                <th className="py-2 pr-4">Ambiente</th>
                <th className="py-2 pr-4">Estado</th>
                <th className="py-2 pr-4">Agente</th>
                <th className="py-2">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {(servers.data ?? []).map((s) => (
                <tr key={s.id} className="border-b border-line last:border-0">
                  <td className="py-3 pr-4 font-medium">{s.hostname}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-ink-3">{s.provider?.kind}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-ink-3">
                    {s.os === 'unknown' ? '—' : s.os}
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs text-ink-3">{s.publicIp ?? '—'}</td>
                  <td className="py-3 pr-4 text-xs text-ink-3">{s.environment}</td>
                  <td className="py-3 pr-4">
                    <PowerPill state={s.powerState} />
                  </td>
                  <td className="py-3 pr-4">
                    <Pill tone={s.managedState === 'managed' ? 'ok' : s.managedState === 'orphaned' ? 'crit' : 'warn'}>
                      {s.managedState === 'managed'
                        ? 'com agente'
                        : s.managedState === 'orphaned'
                          ? 'orfao'
                          : 'sem agente'}
                    </Pill>
                  </td>
                  <td className="py-3">
                    <div className="flex gap-1.5">
                      <Button
                        onClick={() => act(s, 'on')}
                        disabled={!s.can.on || s.powerState === 'on'}
                        title={s.can.on ? 'Ligar' : 'A API deste provedor nao expoe "ligar"'}
                      >
                        Ligar
                      </Button>
                      <Button
                        onClick={() => act(s, 'restart')}
                        disabled={!s.can.restart}
                        title={s.can.restart ? 'Reiniciar' : 'Nao suportado pela API — cai para o agente na Fase 2'}
                      >
                        Reiniciar
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => act(s, 'off')}
                        disabled={!s.can.off || s.powerState === 'off'}
                        title={s.can.off ? 'Desligar' : 'Nao suportado pela API — cai para o agente na Fase 2'}
                      >
                        Desligar
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {servers.data?.length === 0 && !servers.loading && (
          <p className="mt-4 text-sm text-ink-3">
            Nenhum servidor no inventario. Cadastre um provedor e clique em “Sincronizar agora”.
          </p>
        )}
      </Panel>

      {stepUp && <StepUpModal request={stepUp} onClose={() => setStepUp(null)} />}
    </div>
  )
}
